import HttpClient, { ApiResponse } from 'src/app/modules/core/services/httpClient'
import { MarketplaceWarehouse } from '../types/ProductListing'

/**
 * WarehouseService
 *
 * Service for managing marketplace warehouse operations.
 * Provides methods to fetch warehouse data with caching support.
 */
class WarehouseService {
  private static instance: WarehouseService
  private http: HttpClient

  private constructor() {
    this.http = HttpClient.getInstance()
  }

  /**
   * Get singleton instance of WarehouseService
   */
  static getInstance(): WarehouseService {
    if (!this.instance) {
      this.instance = new WarehouseService()
    }
    return this.instance
  }

  /**
   * Fetch marketplace warehouses for a specific account
   *
   * @param accountId - The marketplace account ID (e.g., "acc_shopee_12345")
   * @param isActive - Optional filter for active warehouses only
   * @returns Promise with list of marketplace warehouses
   *
   * @example
   * ```typescript
   * const warehouses = await warehouseService.getMarketplaceWarehouses('acc_shopee_12345', true)
   * ```
   */
  async getMarketplaceWarehouses(
    accountId: string,
    isActive?: boolean
  ): Promise<MarketplaceWarehouse[]> {
    try {
      // Build query params
      const params: Record<string, string> = {}
      if (isActive !== undefined) {
        params.is_active = isActive ? 'true' : 'false'
      }

      console.log('[WarehouseService] 📡 Fetching warehouses:', {
        accountId,
        isActive,
        params,
        note: isActive === true ? '⚠️ FILTERING FOR ACTIVE ONLY' : isActive === false ? '⚠️ FILTERING FOR INACTIVE ONLY' : 'ℹ️ NO FILTER (all warehouses)'
      });

      // API call with 24-hour cache (per API spec)
      const endpoint = `/api/v2/accounts/${accountId}/marketplace_warehouses`
      const result: ApiResponse<{ data: MarketplaceWarehouse[] }> = await this.http.get(
        endpoint,
        params,
        {
          useCache: true, // Enable caching for 24 hours
          cacheTtl: 24 * 60 * 60 * 1000, // 24 hours
        }
      )

      if (!result.success || !result.data) {
        const errorMessage = result.error || 'Failed to fetch marketplace warehouses'
        console.error('[WarehouseService] Error fetching warehouses:', errorMessage)
        throw new Error(errorMessage)
      }

      // Extract warehouses from response
      return result.data.data || []
    } catch (error) {
      console.error('[WarehouseService] Exception in getMarketplaceWarehouses:', error)
      throw error
    }
  }

  /**
   * Clear warehouse cache for a specific account
   * Useful after warehouse sync or updates
   *
   * @param accountId - Optional account ID to clear specific cache
   */
  clearWarehouseCache(accountId?: string): void {
    const pattern = accountId
      ? `/api/v2/accounts/${accountId}/marketplace_warehouses`
      : 'marketplace_warehouses'

    console.log('[WarehouseService] Clearing warehouse cache with pattern:', pattern)
    this.http.clearCache(pattern)
    console.log('[WarehouseService] Cache cleared successfully')
  }

  /**
   * Force sync warehouses from marketplace to server, then fetch fresh data
   * This bypasses the 1-hour sync throttling and returns refreshed warehouse data
   *
   * @param accountId - Marketplace account ID
   * @param isActive - Optional filter for active warehouses only
   * @returns Promise with fresh warehouse data from server
   */
  async syncMarketplaceWarehouses(
    accountId: string,
    isActive?: boolean
  ): Promise<MarketplaceWarehouse[]> {
    try {
      console.log('[WarehouseService] === SYNC & FETCH FLOW START ===')
      console.log('[WarehouseService] Step 1: Forcing warehouse sync for account:', accountId)

      // Step 1: Sync from marketplace to server
      const syncResult = await this.http.post(
        `/api/v2/accounts/${accountId}/marketplace_warehouses/sync`,
        {
          forceSync: true,
          syncOptions: {
            updateExisting: true,
            deactivateMissing: false,
          },
        }
      )

      if (!syncResult.success) {
        throw new Error(syncResult.error || 'Failed to sync warehouses')
      }

      console.log('[WarehouseService] Step 1 Complete: Sync response:', syncResult.data)

      // Step 2: Clear client-side cache to ensure fresh fetch
      console.log('[WarehouseService] Step 2: Clearing client-side cache')
      this.clearWarehouseCache(accountId)

      // Step 3: Fetch fresh data from server (bypassing cache)
      console.log('[WarehouseService] Step 3: Fetching fresh warehouses from server')
      const freshWarehouses = await this.getMarketplaceWarehouses(accountId, isActive)

      console.log('[WarehouseService] Step 3 Complete: Received warehouses:', {
        count: freshWarehouses.length,
        warehouseIds: freshWarehouses.map((wh) => wh.marketplace_warehouse_id),
      })
      console.log('[WarehouseService] === SYNC & FETCH FLOW COMPLETE ===')

      return freshWarehouses
    } catch (error) {
      console.error('[WarehouseService] Sync & fetch failed:', error)
      throw error
    }
  }
}

export default WarehouseService
